// Shared brand constants + reusable layout primitives.
// Keep this the single source of truth for contact details, phone numbers,
// image URLs, WhatsApp link builder and the Lockup component.

import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Phone, Send, MapPin, ShieldCheck, FileCheck2, ChevronDown } from "lucide-react";

// === Brand contact constants ===
export const OFFICE_DISPLAY = "01905 95 95 95";
export const OFFICE_TEL = "01905959595";
export const WHATSAPP_DISPLAY = "07709 10 20 30";
export const WHATSAPP_NUMBER = "447709102030";
export const CONTACT_EMAIL = "info@3countiesrecycling.com";
export const CARRIER_REG = "CBDU646990";
export const COMPANY_NUMBER = "17314994";
export const TRADING_ADDRESS_LINES = [
  "Mill Lane Industrial Estate",
  "Eardiston, Tenbury Wells",
  "Worcestershire",
];

// === Logos ===
export const LOGO_LIGHT_BG_URL = "/brand/logo-stack.png";
export const LOGO_DARK_BG_URL = "/brand/logo-stack.png";

// === Photography ===
export const YARD_IMG_URL =
  "https://customer-assets.emergentagent.com/job_local-collections/artifacts/oj1imxxr_landing%20page%20image.png";
export const AGRI_IMG_URL =
  "https://customer-assets.emergentagent.com/job_local-collections/artifacts/4crs83wo_image.png";
export const AGRI_SILAGE_URL =
  "https://customer-assets.emergentagent.com/job_local-collections/artifacts/cbs55zbl_ChatGPT%20Image%20Jun%2028%2C%202026%2C%2011_17_43%20AM%20%281%29.png";
export const AGRI_SHEETING_URL =
  "https://customer-assets.emergentagent.com/job_local-collections/artifacts/ewzbdllj_ChatGPT%20Image%20Jun%2028%2C%202026%2C%2011_17_44%20AM%20%282%29.png";
export const AGRI_BAGS_URL =
  "https://customer-assets.emergentagent.com/job_local-collections/artifacts/c70ydisj_ChatGPT%20Image%20Jun%2028%2C%202026%2C%2011_17_44%20AM%20%283%29.png";
export const AGRI_NET_URL =
  "https://customer-assets.emergentagent.com/job_local-collections/artifacts/fwjizuci_ChatGPT%20Image%20Jun%2028%2C%202026%2C%2011_17_44%20AM%20%284%29.png";
export const AGRI_DRUMS_URL =
  "https://customer-assets.emergentagent.com/job_local-collections/artifacts/ngu2d8f8_ChatGPT%20Image%20Jun%2028%2C%202026%2C%2011_17_45%20AM%20%285%29.png";
export const AGRI_MIXED_URL =
  "https://customer-assets.emergentagent.com/job_local-collections/artifacts/fv0afbbz_image.png";
export const POLY_IMG_URL =
  "https://customer-assets.emergentagent.com/job_local-collections/artifacts/gf8yuki0_Polystyrene%20image.png";
export const RIGID_PLASTICS_IMG_URL = "/images/plastics-sorted.webp";
export const SECURE_IMG_URL =
  "https://customer-assets.emergentagent.com/job_local-collections/artifacts/4nzwnq7r_Secure%20Destruction%20Image.png";
export const CARDBOARD_IMG_URL =
  "https://customer-assets.emergentagent.com/job_local-collections/artifacts/b2lzxynz_Cardboard.png";
export const TRUCK_CAGE_URL =
  "https://customer-assets.emergentagent.com/job_local-collections/artifacts/d8pytlxa_Cage%20Truck.png";
export const TRUCK_COMPACTOR_URL =
  "https://customer-assets.emergentagent.com/job_local-collections/artifacts/upn2uonb_Refuse%20Truck.png";
export const TRUCK_HOOKLOADER_URL =
  "https://customer-assets.emergentagent.com/job_local-collections/artifacts/n87dik6i_32t%20hookloder.png";
export const RESERVOIR_COMPLETE_URL =
  "https://customer-assets-rejwkqb3.emergentagent.net/job_local-collections/artifacts/fokykv4m_Reservoir%20complete.png";
export const RESERVOIR_SURVEY_URL =
  "https://customer-assets-rejwkqb3.emergentagent.net/job_local-collections/artifacts/0wzobs3q_Reservoir%20Survey.png";
export const RESERVOIR_CONSTRUCTION_URL =
  "https://customer-assets-rejwkqb3.emergentagent.net/job_local-collections/artifacts/prxzl1p8_Reservoir%20Construction.png";
export const AGG_SOIL_SCREENING_URL =
  "https://customer-assets-rejwkqb3.emergentagent.net/job_local-collections/artifacts/ktvlplug_Soil%20Screening.png";
export const AGG_MOBILE_CRUSHING_URL =
  "https://customer-assets-rejwkqb3.emergentagent.net/job_local-collections/artifacts/vxlsr8do_Production%206F2.png";
export const AGG_TRACK_OUTCOME_URL =
  "https://customer-assets-rejwkqb3.emergentagent.net/job_local-collections/artifacts/ldkvyhbh_6F2%20Road.png";

// === WhatsApp link builder ===
export const waLink = (msg) =>
  `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`;

// === Canonical host ===
export const SITE_ORIGIN = "https://3countiesrecycling.com";

// === Per-page SEO hook ===
export const usePageSeo = ({ title, description, path, noindex = false }) => {
  useEffect(() => {
    if (title) document.title = title;
    const setMeta = (name, content, attr = "name") => {
      if (!content) return;
      let tag = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attr, name);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };
    setMeta("description", description);
    setMeta("robots", noindex ? "noindex, follow" : "index, follow");
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    setMeta("og:url", `${SITE_ORIGIN}${path || "/"}`, "property");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `${SITE_ORIGIN}${path || "/"}`);
    window.scrollTo(0, 0);
  }, [title, description, path, noindex]);
};

// === Nav links (kebab-case ids for testids) ===
// The "Recycling" item groups the three material streams; Cardboard &
// Polystyrene sit under it as sub-items in the desktop dropdown / mobile
// accordion. Clicking "Recycling" itself opens the farm-plastic hub page.
export const NAV_LINKS = [
  { label: "Home", to: "/" },
  {
    label: "Recycling",
    to: "/agricultural-recycling",
    children: [
      { label: "Farm plastic", to: "/agricultural-recycling" },
      { label: "Cardboard", to: "/cardboard-recycling" },
      { label: "Rigid plastics & polystyrene", to: "/polystyrene-recycling" },
    ],
  },
  { label: "Reservoirs", to: "/farm-reservoirs" },
  { label: "Aggregates", to: "/aggregates" },
  { label: "Secure destruction", to: "/secure-destruction" },
];

// Flat list of every routed page (used by the footer)
export const FOOTER_LINKS = [
  { label: "Farm plastic recycling", to: "/agricultural-recycling" },
  { label: "Cardboard", to: "/cardboard-recycling" },
  { label: "Rigid plastics & polystyrene", to: "/polystyrene-recycling" },
  { label: "Reservoirs", to: "/farm-reservoirs" },
  { label: "Aggregates", to: "/aggregates" },
  { label: "Secure destruction", to: "/secure-destruction" },
];

// Small helper — sibling links for the Recycling family (used on the
// individual recycling sub-pages to reinforce the grouping).
export const RECYCLING_SIBLINGS = [
  { label: "Farm plastic", to: "/agricultural-recycling" },
  { label: "Cardboard", to: "/cardboard-recycling" },
  { label: "Rigid plastics & polystyrene", to: "/polystyrene-recycling" },
];

// === Brand lockup ===
export const Lockup = ({ iconSize = "h-24", showSubtitle = true }) => (
  <div className="flex items-center gap-4">
    <img
      src={LOGO_DARK_BG_URL}
      alt="Three Counties Recycling"
      className={`${iconSize} w-auto shrink-0 object-contain`}
    />
    {showSubtitle && (
      <p className="hidden md:block text-[10px] uppercase tracking-[0.22em] leading-tight text-white/55 max-w-[160px]">
        Worcestershire · Herefordshire · Gloucestershire
      </p>
    )}
  </div>
);

// === Trust strip ===
export const TrustStrip = ({ variant = "dark", showIcons = true }) => {
  const chip =
    variant === "dark"
      ? "text-white/70"
      : "text-[#4A4A4A]";
  const accent = variant === "dark" ? "text-[#46B649]" : "text-[#024120]";
  const items = [
    { icon: MapPin, label: "Based in Eardiston" },
    { icon: ShieldCheck, label: "Registered waste carrier, broker and dealer" },
    { icon: FileCheck2, label: "Assessment before movement" },
  ];
  return (
    <div
      className={`flex flex-wrap items-center gap-x-6 gap-y-3 text-[11px] uppercase tracking-[0.2em] ${chip}`}
    >
      {items.map((it) => (
        <span key={it.label} data-testid={`trust-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="flex items-center gap-2">
          {showIcons && <it.icon className={`w-4 h-4 ${accent}`} strokeWidth={1.7} />}
          {it.label}
        </span>
      ))}
    </div>
  );
};

// === Section heading (eyebrow + h2) ===
export const SectionHeading = ({ eyebrow, title, accentTitle, intro, tone = "light" }) => {
  const eyebrowColor = tone === "dark" ? "text-[#46B649]" : "text-[#46B649]";
  const titleColor = tone === "dark" ? "text-white" : "text-[#111]";
  const accentColor = tone === "dark" ? "text-[#46B649]" : "text-[#024120]";
  const introColor = tone === "dark" ? "text-white/80" : "text-[#4A4A4A]";
  return (
    <div className="grid md:grid-cols-12 gap-10 items-end mb-12">
      <div className="md:col-span-7">
        <p className={`font-mono text-xs uppercase tracking-[0.28em] ${eyebrowColor}`}>
          {eyebrow}
        </p>
        <h2
          className={`font-display font-black text-4xl md:text-5xl lg:text-[52px] tracking-tight mt-3 leading-[1.05] ${titleColor}`}
        >
          {title}
          {accentTitle && (
            <>
              <br />
              <span className={accentColor}>{accentTitle}</span>
            </>
          )}
        </h2>
      </div>
      {intro && (
        <p className={`md:col-span-5 text-base md:text-lg leading-relaxed ${introColor}`}>
          {intro}
        </p>
      )}
    </div>
  );
};

// === Page hero (compact, brand dark green) ===
export const PageHero = ({ kicker, title, accent, body, image, quoteTo = "/get-a-quote", whatsappMessage }) => (
  <section className="relative overflow-hidden bg-[#024120] text-white">
    {image ? (
      <>
        <img
          src={image}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-45"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#024120] via-[#024120]/85 to-[#024120]/40" />
      </>
    ) : (
      <div className="absolute inset-0 bg-gradient-to-br from-[#024120] via-[#024120] to-[#012b14]" />
    )}
    <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-24 md:pt-28 pb-16 md:pb-24">
      {kicker && (
        <p className="font-mono text-xs uppercase tracking-[0.28em] text-[#FF6600]">
          {kicker}
        </p>
      )}
      <h1
        data-testid="page-h1"
        className="font-display font-black text-4xl md:text-5xl lg:text-[64px] leading-[1.05] tracking-tight mt-5 max-w-4xl"
      >
        {title}
        {accent && (
          <>
            {" "}
            <span className="text-[#46B649]">{accent}</span>
          </>
        )}
      </h1>
      {body && (
        <p className="mt-6 max-w-3xl text-base md:text-lg text-white/85 leading-relaxed">
          {body}
        </p>
      )}
      <div className="mt-8 flex flex-col sm:flex-row gap-4">
        <Link
          to={quoteTo}
          data-testid="page-hero-quote-cta"
          className="inline-flex items-center justify-center gap-2 bg-[#FF6600] hover:bg-[#E55A00] text-white font-bold px-7 py-4 transition-all hover:-translate-y-0.5"
        >
          <Send className="w-4 h-4" /> Get a quote
        </Link>
        <a
          href={whatsappMessage ? waLink(whatsappMessage) : `tel:${OFFICE_TEL}`}
          target={whatsappMessage ? "_blank" : undefined}
          rel={whatsappMessage ? "noreferrer" : undefined}
          data-testid="page-hero-call-cta"
          className="inline-flex items-center justify-center gap-2 border-2 border-white/40 hover:border-white text-white font-bold px-7 py-4 transition-all hover:bg-white/10"
        >
          {whatsappMessage ? <><Send className="w-4 h-4" /> Send photos on WhatsApp</> : <><Phone className="w-4 h-4" /> Call {OFFICE_DISPLAY}</>}
        </a>
      </div>
    </div>
  </section>
);

// === CTA block used at the bottom of most pages ===
export const CtaBlock = ({ title, body, primaryLabel = "Get a quote", showIcons = true, quoteTo = "/get-a-quote", whatsappMessage }) => (
  <section className="bg-[#024120] text-white py-20 md:py-24">
    <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
      <h2 className="font-display font-black text-3xl md:text-5xl tracking-tight leading-[1.1]">
        {title}
      </h2>
      {body && (
        <p className="mt-6 text-base md:text-lg text-white/80 leading-relaxed">
          {body}
        </p>
      )}
      <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
        <Link
          to={quoteTo}
          className="inline-flex items-center justify-center gap-2 bg-[#FF6600] hover:bg-[#E55A00] text-white font-bold px-8 py-4 transition-all hover:-translate-y-1"
          data-testid="cta-quote"
        >
          {showIcons && <Send className="w-4 h-4" />} {primaryLabel}
        </Link>
        <a
          href={whatsappMessage ? waLink(whatsappMessage) : `tel:${OFFICE_TEL}`}
          target={whatsappMessage ? "_blank" : undefined}
          rel={whatsappMessage ? "noreferrer" : undefined}
          className="inline-flex items-center justify-center gap-2 border-2 border-white/40 hover:border-white text-white font-bold px-8 py-4 transition-all hover:bg-white/10"
          data-testid="cta-call"
        >
          {showIcons && (whatsappMessage ? <Send className="w-4 h-4" /> : <Phone className="w-4 h-4" />)} {whatsappMessage ? "Send photos on WhatsApp" : OFFICE_DISPLAY}
        </a>
      </div>
    </div>
  </section>
);

// === Nav ===
export const Nav = ({ showIcons = true }) => {
  const [open, setOpen] = useState(false);
  const [recyclingOpen, setRecyclingOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    setOpen(false);
    setRecyclingOpen(false);
  }, [location.pathname]);

  const isRecyclingRoute = [
    "/agricultural-recycling",
    "/cardboard-recycling",
    "/polystyrene-recycling",
  ].includes(location.pathname);

  return (
    <header
      data-testid="site-nav"
      className="sticky top-0 z-50 bg-[#024120]/95 backdrop-blur-xl border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between gap-6 h-[104px]">
        <Link to="/" className="block" data-testid="brand-logo">
          <Lockup iconSize="h-24" showSubtitle={false} />
        </Link>

        <nav className="hidden lg:flex items-center gap-4 xl:gap-6 text-[13px] font-semibold text-white/85">
          {NAV_LINKS.map((n) =>
            n.children ? (
              <div
                key={n.to}
                className="relative group"
                data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-group`}
              >
                <NavLink
                  to={n.to}
                  end={n.to === "/"}
                  className={`flex items-center gap-1 whitespace-nowrap transition-colors ${
                    isRecyclingRoute
                      ? "text-[#46B649]"
                      : "hover:text-[#46B649]"
                  }`}
                  data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  {n.label}
                  {showIcons && <ChevronDown className="w-3.5 h-3.5 transition-transform group-hover:rotate-180" strokeWidth={2.5} />}
                </NavLink>
                <div
                  className="invisible opacity-0 translate-y-1 pointer-events-none group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto group-focus-within:visible group-focus-within:opacity-100 group-focus-within:translate-y-0 group-focus-within:pointer-events-auto absolute top-full left-0 pt-3 transition-all duration-200 z-50"
                  data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-dropdown`}
                >
                  <div className="w-64 bg-[#024120] border border-white/10 shadow-[0_20px_50px_-15px_rgba(0,0,0,0.6)]">
                    {n.children.map((c) => (
                      <NavLink
                        key={c.to}
                        to={c.to}
                        end
                        data-testid={`nav-sub-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className={({ isActive }) =>
                          `block px-5 py-3 text-[13px] border-b border-white/5 last:border-b-0 transition-colors ${
                            isActive
                              ? "text-[#46B649] bg-white/5"
                              : "text-white/85 hover:text-[#46B649] hover:bg-white/5"
                          }`
                        }
                      >
                        {c.label}
                      </NavLink>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                className={({ isActive }) =>
                  `whitespace-nowrap transition-colors ${
                    isActive ? "text-[#46B649]" : "hover:text-[#46B649]"
                  }`
                }
                data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                {n.label}
              </NavLink>
            )
          )}
        </nav>

        <Link
          to="/get-a-quote"
          className="hidden md:inline-flex items-center gap-2 bg-[#FF6600] hover:bg-[#E55A00] text-white font-bold text-sm px-5 py-2.5 transition-all hover:-translate-y-0.5 whitespace-nowrap"
          data-testid="nav-quote-cta"
        >
          {showIcons && <Send className="w-4 h-4" />} Get a quote
        </Link>

        <button
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
          aria-expanded={open}
          aria-controls="mobile-menu"
          className="lg:hidden inline-flex h-10 w-10 items-center justify-center border border-white/30"
          data-testid="mobile-menu-toggle"
        >
          <span className="flex flex-col gap-1">
            <span className="block h-[2px] w-5 bg-white" />
            <span className="block h-[2px] w-5 bg-white" />
            <span className="block h-[2px] w-5 bg-white" />
          </span>
        </button>
      </div>

      {open && (
        <div id="mobile-menu" className="lg:hidden border-t border-white/10 bg-[#024120] max-h-[calc(100dvh-104px)] overflow-y-auto" data-testid="mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-1 text-sm font-semibold text-white">
            {NAV_LINKS.map((n) =>
              n.children ? (
                <div key={n.to} className="border-b border-white/5">
                  <button
                    type="button"
                    onClick={() => setRecyclingOpen((o) => !o)}
                    aria-expanded={recyclingOpen}
                    aria-controls="mobile-recycling-links"
                    data-testid={`mobile-nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-toggle`}
                    className={`w-full flex items-center justify-between py-3 ${
                      isRecyclingRoute ? "text-[#46B649]" : ""
                    }`}
                  >
                    <span>{n.label}</span>
                    {showIcons ? <ChevronDown
                      className={`w-4 h-4 transition-transform ${
                        recyclingOpen ? "rotate-180" : ""
                      }`}
                      strokeWidth={2.5}
                    /> : <span className="text-xs font-normal">{recyclingOpen ? "Hide" : "Show"}</span>}
                  </button>
                  {recyclingOpen && (
                    <div id="mobile-recycling-links" className="pb-3 pl-4 flex flex-col gap-1 border-l-2 border-[#46B649] ml-2">
                      {n.children.map((c) => (
                        <NavLink
                          key={c.to}
                          to={c.to}
                          end
                          data-testid={`mobile-nav-sub-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          className={({ isActive }) =>
                            `py-2 pl-3 text-[13px] font-medium text-white/85 ${
                              isActive ? "text-[#46B649]" : ""
                            }`
                          }
                        >
                          {c.label}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={({ isActive }) =>
                    `py-3 border-b border-white/5 ${
                      isActive ? "text-[#46B649]" : ""
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              )
            )}
            <Link
              to="/get-a-quote"
              data-testid="mobile-nav-quote-cta"
              className="mt-3 inline-flex items-center justify-center gap-2 bg-[#FF6600] text-white font-bold px-5 py-3"
            >
              {showIcons && <Send className="w-4 h-4" />} Get a quote
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

// === Recycling sub-nav (used on the 3 recycling material pages) ===
export const RecyclingSubNav = () => {
  const location = useLocation();
  return (
    <div
      data-testid="recycling-sub-nav"
      className="bg-[#F2F2F2] border-b border-[#024120]/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex flex-wrap items-center gap-x-2 gap-y-2">
        <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#4A4A4A] mr-3">
          / Recycling
        </span>
        {RECYCLING_SIBLINGS.map((s) => {
          const isActive = location.pathname === s.to;
          return (
            <Link
              key={s.to}
              to={s.to}
              aria-current={isActive ? "page" : undefined}
              data-testid={`recycling-sub-${s.label
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")}`}
              className={`px-4 py-2 text-[13px] font-semibold transition-colors ${
                isActive
                  ? "bg-[#024120] text-white"
                  : "bg-white text-[#024120] hover:bg-[#024120] hover:text-white"
              }`}
            >
              {s.label}
            </Link>
          );
        })}
      </div>
    </div>
  );
};

// === Footer ===
export const Footer = () => (
  <footer className="bg-[#0D2A20] text-white/85">
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20">
      <div className="grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <Lockup iconSize="h-24" showSubtitle={true} />
          <p className="mt-6 text-sm text-white/70 leading-relaxed max-w-md">
            Practical recycling, material recovery and rural infrastructure
            support across Worcestershire, Herefordshire and Gloucestershire.
          </p>
        </div>

        <div className="md:col-span-3">
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#46B649]">
            / Services
          </p>
          <ul className="mt-4 space-y-2 text-sm">
            {FOOTER_LINKS.map((n) => (
              <li key={n.to}>
                <Link
                  to={n.to}
                  data-testid={`footer-service-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="hover:text-[#FF6600] transition-colors"
                >
                  {n.label}
                </Link>
              </li>
            ))}
            <li>
              <Link
                to="/get-a-quote"
                data-testid="footer-service-quote"
                className="hover:text-[#FF6600] transition-colors font-semibold"
              >
                Get a quote
              </Link>
            </li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#46B649]">
            / Contact
          </p>
          <ul className="mt-4 space-y-2 text-sm">
            <li>
              <a
                href={`tel:${OFFICE_TEL}`}
                data-testid="footer-phone-link"
                className="hover:text-[#FF6600] transition-colors"
              >
                {OFFICE_DISPLAY}
              </a>
            </li>
            <li>
              <a
                href={waLink("Hi 3 Counties Recycling, please can I get a quote.")}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-whatsapp-link"
                className="hover:text-[#FF6600] transition-colors"
              >
                WhatsApp or text: {WHATSAPP_DISPLAY}
              </a>
            </li>
            <li>
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                data-testid="footer-email-link"
                className="hover:text-[#FF6600] transition-colors"
              >
                {CONTACT_EMAIL}
              </a>
            </li>
          </ul>
          <p data-testid="footer-trading-address" className="mt-6 text-xs text-white/60 leading-relaxed">
            {TRADING_ADDRESS_LINES.join(", ")}
            <br />
            Visits by prior arrangement only.
          </p>
        </div>
      </div>

      <div className="mt-14 pt-8 border-t border-white/10 grid md:grid-cols-12 gap-6 text-xs text-white/50">
        <p data-testid="footer-company-registration" className="md:col-span-8 leading-relaxed">
          3 Counties Recycling is a trading name of Three Counties Recycling
          Ltd. Registered in England and Wales, company number {COMPANY_NUMBER}.
          Registered waste carrier, broker and dealer: {CARRIER_REG}. Waste
          carrier registration is renewed every three years. Carrier
          registration does not by itself authorise storage or treatment at a
          site.
        </p>
        <p className="md:col-span-4 md:text-right space-x-4">
          <Link
            to="/privacy"
            className="hover:text-[#FF6600]"
            data-testid="footer-privacy-link"
          >
            Privacy notice
          </Link>
        </p>
      </div>
      <p className="mt-4 text-[11px] text-white/40">
        © {new Date().getFullYear()} Three Counties Recycling Ltd. All rights
        reserved.
      </p>
    </div>
  </footer>
);
